import React from "react";
import { ArrowUpDown, ArrowUp, ArrowDown, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

// Komponen tabel daftar bersama: sticky header, horizontal scroll, sort, states, pagination footer.
// columns: [{ key, label, align?, className?, width?, sortable?, render?(row) }]
export function DataTable({
  columns, rows, loading, error, sort, onSort, rowKey = (r) => r.id,
  page = 1, pages = 1, total = 0, limit = 25, onPageChange, onLimitChange,
  emptyLabel = "Belum ada data.", searching = false, testid = "data-table",
}) {
  const showEmpty = !loading && !error && rows.length === 0;
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const alignCls = (a) => (a === "right" ? "text-right" : a === "center" ? "text-center" : "text-left");

  return (
    <div data-testid={testid}>
      <div className="max-h-[65vh] overflow-auto rounded-lg border border-taupe/15">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-ivory">
            <TableRow className="hover:bg-transparent">
              {columns.map((c) => (
                <TableHead key={c.key} style={c.width ? { width: c.width, minWidth: c.width } : undefined}
                  className={`${alignCls(c.align)} ${c.className || ""} whitespace-nowrap bg-ivory`}>
                  {c.sortable && onSort ? (
                    <button type="button" onClick={() => onSort(c.key)} className="inline-flex items-center gap-1 hover:text-cocoa font-medium" data-testid={`sort-${c.key}`}>
                      {c.label}
                      {sort?.sort === c.key ? (sort.dir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />) : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                    </button>
                  ) : c.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-wmuted py-12"><Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />{searching ? "Mencari…" : "Memuat…"}</TableCell></TableRow>
            )}
            {error && !loading && (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-status-reject py-12" data-testid="dt-error">Gagal memuat data. Coba muat ulang.</TableCell></TableRow>
            )}
            {showEmpty && (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-wmuted py-12" data-testid="dt-empty">{emptyLabel}</TableCell></TableRow>
            )}
            {!loading && !error && rows.map((r) => (
              <TableRow key={rowKey(r)} data-testid={`dt-row-${rowKey(r)}`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={`${alignCls(c.align)} ${c.cellClassName || ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? "-")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 text-sm">
        <div className="flex items-center gap-2 text-wmuted">
          <span>Baris per halaman</span>
          <Select value={String(limit)} onValueChange={(v) => onLimitChange && onLimitChange(parseInt(v))}>
            <SelectTrigger className="h-8 w-20" data-testid="dt-limit"><SelectValue /></SelectTrigger>
            <SelectContent>{[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <span data-testid="dt-count">Menampilkan {from.toLocaleString("id-ID")}–{to.toLocaleString("id-ID")} dari {total.toLocaleString("id-ID")} hasil</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="border-taupe text-cocoa" disabled={page <= 1 || loading} onClick={() => onPageChange && onPageChange(page - 1)} data-testid="dt-prev">Sebelumnya</Button>
          <span className="text-wmuted" data-testid="dt-page">Hal. {page} / {pages}</span>
          <Button size="sm" variant="outline" className="border-taupe text-cocoa" disabled={page >= pages || loading} onClick={() => onPageChange && onPageChange(page + 1)} data-testid="dt-next">Berikutnya</Button>
        </div>
      </div>
    </div>
  );
}

// Chip filter aktif + tombol reset.
export function ActiveFilters({ chips, onReset }) {
  if (!chips || !chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="active-filters">
      {chips.map((c) => (
        <span key={c.key} className="text-xs bg-taupe/15 text-cocoa px-2 py-1 rounded-full inline-flex items-center gap-1">
          {c.label}{c.onClear && <button type="button" onClick={c.onClear} className="text-cocoa/70 hover:text-cocoa" data-testid={`chip-clear-${c.key}`}>×</button>}
        </span>
      ))}
      {onReset && <button type="button" onClick={onReset} className="text-xs text-status-reject underline" data-testid="filters-reset">Reset filter</button>}
    </div>
  );
}
